/** Logged-in user fields sent to the report viewer. */
export interface ReportViewerUser {
  customerId: unknown;
  userId: unknown;
  customerName: unknown;
  role: unknown;
}

/** Inputs for {@link buildReportViewerNavigation}. */
export interface ReportViewerNavigationInput {
  // Report configuration row from the report-viewer API; its shape varies per report.
  cfg: any;
  /** Report type identifier. */
  reportType: string;
  /** Start date, already formatted for the viewer. */
  startDate: string;
  /** End date, already formatted for the viewer. */
  endDate: string;
  /** Selected report category: `'S'` statutory or `'I'` internal. */
  reportCategory: string;
  /** Checklist parameter; `'L'` selects the license variant. */
  clParameterValue: string;
  /** Whether the screen shows (and filters by) the location dropdown. */
  showLocationDropdown: boolean;
  // Selected location: a tree node ({ ID } or { id }) or a bare id, depending on the dropdown.
  selectedLocation: any;
  /** Logged-in user fields. */
  user: ReportViewerUser;
}

/**
 * Builds the navigation payload handed to the report viewer route.
 *
 * Without the location dropdown it carries only the user/date viewer params.
 * With it, the selected location id (`-1` when none) is added as `entityID`
 * for internal license reports that have no customer on the config, otherwise
 * as `Entity`, and the selected location itself is passed along.
 * @param input - Report config, dates, filters and user fields.
 * @returns The navigation data, keys in the order the viewer has always received them.
 */
// Returns `any`: the viewer route reads this untyped from router state, as before.
export function buildReportViewerNavigation(input: ReportViewerNavigationInput): any {
  const { cfg, reportType, startDate, endDate, reportCategory, clParameterValue, selectedLocation, user } = input;

  const baseViewerParams = {
    Customerid:   user.customerId,
    UserID:       user.userId,
    CustomerName: user.customerName,
    Flag:         user.role,
    StartDate:    startDate,
    EndDate:      endDate,
  };

  const baseNavData = {
    reportConfig: cfg, reportType,
    reportCategory, startDate, endDate
  };

  if (!input.showLocationDropdown) {
    return { ...baseNavData, viewerParams: baseViewerParams };
  }

  const field    = reportCategory === 'I' && clParameterValue === 'L' && !cfg.CustomerID ? 'entityID' : 'Entity';
  const entityid = selectedLocation?.ID ?? selectedLocation?.id ?? selectedLocation ?? -1;

  return {
    ...baseNavData,
    viewerParams:     { ...baseViewerParams, [field]: entityid },
    selectedLocation
  };
}
